import React, { useCallback, useState } from "react";
import { Button, DetailCard } from "@bloomberg/media.atomics";

import HoldingsRow, { type HoldingRowState } from "./HoldingsRow";
import styles from "./styles.module.css";

type InitialLot = {
  readonly id: number;
  readonly shares: number;
  readonly buyPrice: number;
  readonly buyDate: Date | string;
};

type TickerHoldingsCardProps = {
  readonly ticker: string;
  readonly companyName: string;
  readonly initialLots: ReadonlyArray<InitialLot>;
  readonly savedRows?: ReadonlyArray<HoldingRowState>;
  readonly onChange: (
    ticker: string,
    holdings: ReadonlyArray<HoldingRowState>
  ) => void;
  readonly onDelete?: () => void;
};

function formatDate(date: Date | string): string {
  const d = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(d.getTime())) return String(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function toRowState(lot: InitialLot): HoldingRowState {
  return {
    id: String(lot.id),
    date: formatDate(lot.buyDate),
    shares: String(lot.shares),
    price: String(lot.buyPrice),
  };
}

export default function TickerHoldingsCard({
  ticker,
  companyName,
  initialLots,
  savedRows,
  onChange,
  onDelete,
}: TickerHoldingsCardProps) {
  const [rows, setRows] = useState<HoldingRowState[]>(() =>
    savedRows ? [...savedRows] : initialLots.map(toRowState)
  );

  const updateRows = useCallback(
    (next: HoldingRowState[]) => {
      setRows(next);
      onChange(ticker, next);
    },
    [onChange, ticker]
  );

  const handleDateChange = useCallback(
    (id: string, value: string) => {
      updateRows(rows.map((r) => (r.id === id ? { ...r, date: value } : r)));
    },
    [rows, updateRows]
  );

  const handleSharesChange = useCallback(
    (id: string, value: string) => {
      updateRows(rows.map((r) => (r.id === id ? { ...r, shares: value } : r)));
    },
    [rows, updateRows]
  );

  const handlePriceChange = useCallback(
    (id: string, value: string) => {
      updateRows(rows.map((r) => (r.id === id ? { ...r, price: value } : r)));
    },
    [rows, updateRows]
  );

  const handleRemove = useCallback(
    (id: string) => {
      updateRows(rows.filter((r) => r.id !== id));
    },
    [rows, updateRows]
  );

  const handleAddRow = useCallback(() => {
    updateRows([
      ...rows,
      { id: crypto.randomUUID(), date: "", shares: "", price: "" },
    ]);
  }, [rows, updateRows]);

  const disableRemove = rows.length <= 1;

  return (
    <div className={styles.tickerCard}>
      <div className={styles.tickerCardTitle}>
        <div className={styles.tickerInfo}>
          <span className={styles.tickerSymbol}>{ticker}</span>
          {companyName ? (
            <span className={styles.tickerCompany}>{companyName}</span>
          ) : null}
        </div>
        <div className={styles.tickerActions}>
          <Button
            type="button"
            variant="fill"
            size="medium"
            onClick={handleAddRow}
          >
            Add Lot
          </Button>
          <Button
            type="button"
            variant="outline"
            size="medium"
            onClick={onDelete}
          >
            Delete Ticker
          </Button>
        </div>
      </div>
      <DetailCard
        className={styles.holdingsDetailCard}
        orientation="vertical"
        header={
          <div className={styles.holdingsHeaders}>
            <span className={styles.holdingsHeaderLabel}>Date</span>
            <span className={styles.holdingsHeaderLabel}>Shares</span>
            <span className={styles.holdingsHeaderLabel}>Price</span>
            <span />
          </div>
        }
        body={
          <>
            {rows.map((row) => (
              <HoldingsRow
                key={row.id}
                holding={row}
                onDateChange={handleDateChange}
                onSharesChange={handleSharesChange}
                onPriceChange={handlePriceChange}
                onRemove={handleRemove}
                disableRemove={disableRemove}
              />
            ))}
          </>
        }
      />
    </div>
  );
}
